import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../shared/components/Modal';
import { KycVerification } from '../../shared/types';
import { approveKyc, assignKyc, getKyc, rejectKyc } from './api';
import { deviceLabel } from './device';
import AuthImage from './AuthImage';

interface KycReviewModalProps {
  kycId: number | null;
  onClose: () => void;
  onUpdated: () => void;
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-warning',
  under_review: 'badge-info',
  approved: 'badge-success',
  rejected: 'badge-danger',
  resubmit_required: 'badge-warning',
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 py-1.5">
    <dt className="text-xs text-slate-500 uppercase tracking-wide">{label}</dt>
    <dd className="col-span-2 text-sm text-slate-200">{value || <span className="text-slate-600">—</span>}</dd>
  </div>
);

const KycReviewModal: React.FC<KycReviewModalProps> = ({ kycId, onClose, onUpdated }) => {
  const [kyc, setKyc] = useState<KycVerification | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<'assign' | 'approve' | 'reject' | 'resubmit' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [zoomPath, setZoomPath] = useState<string | null>(null);

  const isOpen = kycId !== null;

  const load = useCallback(async () => {
    if (!kycId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getKyc(kycId);
      setKyc(res);
      setInternalNotes(res.internal_notes ?? '');
      setRejectionReason('');
      setShowRejectForm(false);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load')
        : 'Failed to load';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [kycId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const handleAssign = async () => {
    if (!kyc) return;
    setActionInProgress('assign');
    setError(null);
    try {
      const updated = await assignKyc(kyc.id);
      setKyc(updated);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to assign')
        : 'Failed to assign';
      setError(msg);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleApprove = async () => {
    if (!kyc) return;
    if (!window.confirm(`Approve KYC #${kyc.id} for user #${kyc.user_id}?`)) return;
    setActionInProgress('approve');
    setError(null);
    try {
      await approveKyc(kyc.id, internalNotes.trim() || undefined);
      onUpdated();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to approve')
        : 'Failed to approve';
      setError(msg);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (requestResubmit: boolean) => {
    if (!kyc) return;
    if (!rejectionReason.trim()) {
      setError('Please provide a reason');
      return;
    }
    setActionInProgress(requestResubmit ? 'resubmit' : 'reject');
    setError(null);
    try {
      await rejectKyc(
        kyc.id,
        rejectionReason.trim(),
        requestResubmit,
        internalNotes.trim() || undefined
      );
      onUpdated();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to reject')
        : 'Failed to reject';
      setError(msg);
    } finally {
      setActionInProgress(null);
    }
  };

  const isFinal = kyc?.status === 'approved';

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={kyc ? `KYC #${kyc.id} — ${kyc.first_name} ${kyc.last_name}` : 'KYC Review'}
        size="3xl"
      >
        {loading || !kyc ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <span className={`badge ${STATUS_BADGE[kyc.status] ?? 'badge-info'}`}>
                {kyc.status.replace(/_/g, ' ')}
              </span>
              <span className="badge badge-info capitalize">{kyc.level}</span>
              <span className="text-xs text-slate-500">
                Submitted: {kyc.submitted_at ? new Date(kyc.submitted_at).toLocaleString() : '—'}
              </span>
              {kyc.reviewed_at && (
                <span className="text-xs text-slate-500">
                  Reviewed: {new Date(kyc.reviewed_at).toLocaleString()}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-2">
                  Account
                </h3>
                <dl>
                  <InfoRow label="User ID" value={<span className="font-mono">#{kyc.user_id}</span>} />
                  <InfoRow label="Email" value={kyc.user?.email} />
                  <InfoRow label="Username" value={kyc.user?.username} />
                  <InfoRow label="Phone" value={kyc.user?.phone_number} />
                  <InfoRow
                    label="Registered"
                    value={kyc.user?.created_at ? new Date(kyc.user.created_at).toLocaleString() : null}
                  />
                </dl>

                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mt-5 mb-2">
                  Submission
                </h3>
                <dl>
                  <InfoRow
                    label="IP address"
                    value={kyc.ip_address ? <span className="font-mono">{kyc.ip_address}</span> : null}
                  />
                  <InfoRow label="Device" value={deviceLabel(kyc)} />
                  <InfoRow
                    label="User agent"
                    value={kyc.user_agent ? (
                      <span className="text-xs text-slate-400 break-all">{kyc.user_agent}</span>
                    ) : null}
                  />
                </dl>

                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mt-5 mb-2">
                  Personal
                </h3>
                <dl>
                  <InfoRow
                    label="Full name"
                    value={`${kyc.first_name} ${kyc.middle_name ?? ''} ${kyc.last_name}`.replace(/\s+/g, ' ').trim()}
                  />
                  <InfoRow label="Date of birth" value={kyc.date_of_birth} />
                  <InfoRow label="Nationality" value={kyc.nationality} />
                  <InfoRow label="Residence" value={kyc.country_of_residence} />
                  <InfoRow label="City" value={kyc.city} />
                  <InfoRow label="Postal code" value={kyc.postal_code} />
                  <InfoRow label="Address" value={kyc.address} />
                </dl>

                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mt-5 mb-2">
                  Document
                </h3>
                <dl>
                  <InfoRow
                    label="Type"
                    value={<span className="capitalize">{kyc.document_type.replace('_', ' ')}</span>}
                  />
                  <InfoRow
                    label="Number"
                    value={<span className="font-mono">{kyc.document_number}</span>}
                  />
                  <InfoRow label="Country" value={kyc.document_country} />
                  <InfoRow label="Expires" value={kyc.document_expiry_date} />
                </dl>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-2">
                  Uploaded files
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Document — front</p>
                    <AuthImage
                      path={kyc.document_front_path ?? null}
                      alt="Document front"
                      className="w-full h-48 object-contain bg-slate-800 rounded-lg border border-slate-700"
                      onClick={() => kyc.document_front_path && setZoomPath(kyc.document_front_path)}
                    />
                  </div>
                  {kyc.document_back_path && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Document — back</p>
                      <AuthImage
                        path={kyc.document_back_path}
                        alt="Document back"
                        className="w-full h-48 object-contain bg-slate-800 rounded-lg border border-slate-700"
                        onClick={() => setZoomPath(kyc.document_back_path!)}
                      />
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Selfie with document</p>
                    <AuthImage
                      path={kyc.selfie_path ?? null}
                      alt="Selfie"
                      className="w-full h-48 object-contain bg-slate-800 rounded-lg border border-slate-700"
                      onClick={() => kyc.selfie_path && setZoomPath(kyc.selfie_path)}
                    />
                  </div>
                  {kyc.proof_of_address_path && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Proof of address</p>
                      <AuthImage
                        path={kyc.proof_of_address_path}
                        alt="Proof of address"
                        className="w-full h-48 object-contain bg-slate-800 rounded-lg border border-slate-700"
                        onClick={() => setZoomPath(kyc.proof_of_address_path!)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {kyc.rejection_reason && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-1">
                  Rejection reason
                </p>
                <p className="text-sm text-red-300">{kyc.rejection_reason}</p>
              </div>
            )}

            {!isFinal && (
              <div className="space-y-3 pt-3 border-t border-slate-800">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">
                    Internal notes (not shown to user)
                  </label>
                  <textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    className="input min-h-[60px]"
                    placeholder="Optional notes for other reviewers"
                  />
                </div>

                {showRejectForm && (
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">
                      Reason (visible to user) <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="input min-h-[80px]"
                      placeholder="Document is unclear, name does not match, expired, etc."
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-2 justify-end">
                  {kyc.status === 'pending' && (
                    <button
                      onClick={handleAssign}
                      disabled={actionInProgress !== null}
                      className="btn btn-secondary"
                    >
                      {actionInProgress === 'assign' ? 'Claiming...' : 'Claim for review'}
                    </button>
                  )}
                  {!showRejectForm ? (
                    <>
                      <button
                        onClick={() => setShowRejectForm(true)}
                        disabled={actionInProgress !== null}
                        className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition"
                      >
                        Reject / Request resubmit
                      </button>
                      <button
                        onClick={handleApprove}
                        disabled={actionInProgress !== null}
                        className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition disabled:opacity-50"
                      >
                        {actionInProgress === 'approve' ? 'Approving...' : 'Approve'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowRejectForm(false)}
                        disabled={actionInProgress !== null}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleReject(true)}
                        disabled={actionInProgress !== null || !rejectionReason.trim()}
                        className="px-4 py-2 text-sm font-medium text-yellow-400 hover:text-yellow-300 bg-yellow-500/10 hover:bg-yellow-500/20 rounded-lg transition disabled:opacity-50"
                      >
                        {actionInProgress === 'resubmit' ? 'Sending...' : 'Request resubmit'}
                      </button>
                      <button
                        onClick={() => handleReject(false)}
                        disabled={actionInProgress !== null || !rejectionReason.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition disabled:opacity-50"
                      >
                        {actionInProgress === 'reject' ? 'Rejecting...' : 'Reject'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {zoomPath && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6"
          onClick={() => setZoomPath(null)}
        >
          <div className="max-w-5xl w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <AuthImage
              path={zoomPath}
              alt="Zoom"
              className="w-full max-h-[85vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setZoomPath(null)}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-slate-900 border border-slate-700 text-white text-lg"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default KycReviewModal;
